import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash/debounce";
import isEqual from "lodash/isEqual";
import styled from "styled-components";
import { Button } from "components/bootstrap";
import TypeAheadInput from "components/common/TypeAheadInput";
const StyledButton = styled(Button)`
  margin-left: 5px;
`;
const TypeAheadDataFilter = ({
  id = "",
  data = [],
  filterData: filterDataOverride = void 0,
  label = "",
  onDataFiltered = void 0,
  searchInKeys = [],
  debounceMs = 250
}) => {
  const [filterText, setFilterText] = useState("");
  const filterTextRef = useRef("");
  const previousDataRef = useRef(data);
  const typeAheadInputRef = useRef(null);
  const setFilterTextValue = useCallback((value) => {
    filterTextRef.current = value;
    setFilterText(value);
  }, []);
  const matchStringSearch = useCallback(
    (datum) => searchInKeys.some((searchInKey) => {
      const key = datum[searchInKey];
      const value = filterTextRef.current;
      if (key === null) {
        return false;
      }
      const containsFilter = (entry, thisValue) => {
        if (typeof entry === "undefined") {
          return false;
        }
        return entry.toLocaleLowerCase().indexOf(thisValue.toLocaleLowerCase()) !== -1;
      };
      if (typeof key === "object") {
        return key.some((arrayEntry) => containsFilter(arrayEntry, value));
      }
      return containsFilter(key, value);
    }),
    [searchInKeys]
  );
  const filterData = useCallback(() => {
    if (typeof filterDataOverride === "function") {
      return filterDataOverride(data);
    }
    const filteredData = data.filter((datum) => matchStringSearch(datum));
    const currentFilterText = filterTextRef.current;
    onDataFiltered(filteredData, currentFilterText);
    return true;
  }, [data, filterDataOverride, matchStringSearch, onDataFiltered]);
  const filterDataRef = useRef(filterData);
  useEffect(() => {
    filterDataRef.current = filterData;
  }, [filterData]);
  const debouncedFilterDataRef = useRef(null);
  useEffect(() => {
    const debounced = debounce(() => filterDataRef.current(), debounceMs);
    debouncedFilterDataRef.current = debounced;
    return () => debounced.cancel();
  }, [debounceMs]);
  useEffect(() => {
    if (!isEqual(previousDataRef.current, data)) {
      previousDataRef.current = data;
      filterDataRef.current();
    }
  }, [data]);
  const onFilterTextChanged = useCallback(
    (value) => {
      setFilterTextValue(value);
      const debounced = debouncedFilterDataRef.current;
      if (debounced) {
        debounced();
        return;
      }
      filterDataRef.current();
    },
    [setFilterTextValue]
  );
  const applyFilters = useCallback(() => {
    const debounced = debouncedFilterDataRef.current;
    if (debounced) {
      debounced.flush();
      return;
    }
    filterDataRef.current();
  }, []);
  const onFilterKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        applyFilters();
      }
    },
    [applyFilters]
  );
  const resetFilters = useCallback(() => {
    var _a, _b;
    (_a = debouncedFilterDataRef.current) == null ? void 0 : _a.cancel();
    (_b = typeAheadInputRef.current) == null ? void 0 : _b.clear();
    setFilterTextValue("");
    filterDataRef.current();
  }, [setFilterTextValue]);
  return /* @__PURE__ */ React.createElement("div", { className: "filter" }, /* @__PURE__ */ React.createElement("div", { className: "form-inline", style: { display: "inline-flex", alignItems: "flex-end" }, role: "search" }, /* @__PURE__ */ React.createElement(
    TypeAheadInput,
    {
      id,
      ref: typeAheadInputRef,
      formGroupClassName: "",
      label,
      onChange: onFilterTextChanged,
      onKeyDown: onFilterKeyDown
    }
  ), /* @__PURE__ */ React.createElement(StyledButton, { type: "button", onClick: applyFilters }, "Filter"), /* @__PURE__ */ React.createElement(StyledButton, { type: "button", onClick: resetFilters, disabled: filterText === "" }, "Reset")));
};
export default TypeAheadDataFilter;
